/**
 * 项目 API 服务
 * 对接后端 /api/v1/projects
 */
import apiClient from './client';
import { keysToCamelCase } from '@/lib/api-utils';
import type { SubprojectBootstrapPayload } from '@/types/screenplay';
import type {
  BatchAddSubprojectMembersRequest,
  BatchAddSubprojectMembersResponse,
  BatchRemoveSubprojectMembersRequest,
  BatchRemoveSubprojectMembersResponse,
  BatchUpdateSubprojectMemberRolesRequest,
  BatchUpdateSubprojectMemberRolesResponse,
  CollaboratorOrg,
  CollaboratorOrgMemberCandidateListResponse,
  CollaboratorOrgListResponse,
  ContractorSettlementReportDetail,
  ContractorSettlementReportListResponse,
  ContractorSettlementReportTerminateDto,
  ContractorSettlementReportTerminateResponse,
  AddCollaboratorOrgMemberDto,
  BatchInviteCollaboratorOrgDto,
  BatchInviteCollaboratorOrgResult,
  ArchiveProjectDto,
  ArchiveProjectResponse,
  CreateProjectDto,
  CreateProjectExportResponse,
  CreateSubprojectMemberSource,
  DirectorBaseSetupRequest,
  DirectorBaseSetupResponse,
  FunctionalRoleDefinition,
  InviteCollaboratorOrgDto,
  UpdateCollaboratorOrgAllowedFunctionalRolesDto,
  ProjectExportRequest,
  ProjectExportCapabilitiesResponse,
  MoveProjectDto,
  MemberSetupCompleteRequest,
  MemberSetupCompleteResponse,
  MemberConfigInheritanceRequest,
  MemberConfigInheritanceSummary,
  PendingCollaborationListResponse,
  PauseProjectDto,
  PauseProjectResponse,
  Project,
  ProjectEntryContext,
  ProjectListParams,
  DirectorySubprojectSort,
  DirectorySubprojectsResponse,
  ParentDirectoryResponse,
  ProjectListResponse,
  ProjectExportDownloadUrlResponse,
  ProjectExportStatusResponse,
  ProjectMember,
  ProjectMemberListResponse,
  ProjectPermissionGroupOption,
  ParentProjectMemberManagementContext,
  ProjectProducerReplaceResponse,
  ProducerSetupRequest,
  ProducerSetupResponse,
  ReplaceProjectProducerDto,
  ProjectShare,
  ProjectShareListResponse,
  PublicProjectShareAccess,
  PublicProjectShareInfo,
  RestoreProjectResponse,
  SetProjectCoverImageDto,
  SharePermission,
  SubprojectMemberTargetListResponse,
  TransferProjectToOrgDto,
  TransferProjectToOrgResponse,
  TrashListResponse,
  UpdateCollaboratorOrgMemberRolesDto,
  UpdateProjectDto,
} from '@/types';

export interface EpisodeDeleteOptions {
  deleteCreatedAssets?: boolean;
  deleteCreatedMaterials?: boolean;
}

export interface EpisodeDeletePreviewAsset {
  id: string;
  name: string;
  type: 'character' | 'location' | 'prop' | string;
}

export interface EpisodeDeletePreviewMaterial {
  id: string;
  name: string;
  type: string;
  elementTagId?: string | null;
}

export interface EpisodeDeletePreview {
  createdAssets: {
    characters: EpisodeDeletePreviewAsset[];
    locations: EpisodeDeletePreviewAsset[];
    props: EpisodeDeletePreviewAsset[];
  };
  createdMaterials: EpisodeDeletePreviewMaterial[];
  materialsUnderCreatedAssets?: EpisodeDeletePreviewMaterial[];
  preservedMaterialsUnderCreatedAssets: EpisodeDeletePreviewMaterial[];
  crossEpisodeUsages: Array<{
    elementType: number;
    elementId: string;
    screenplayId: string;
  }>;
  counts: {
    characters: number;
    locations: number;
    props: number;
    materials: number;
    materialsUnderCreatedAssets?: number;
    preservedMaterialsUnderCreatedAssets: number;
    crossEpisodeUsages: number;
  };
}

/**
 * 项目 API
 */
export const projectApi = {
  /**
   * 获取项目列表
   * GET /api/v1/projects
   */
  async getAll(
    params?: ProjectListParams,
    request?: { activeOrgId?: string | null }
  ): Promise<ProjectListResponse> {
    const activeOrgId = request?.activeOrgId;
    const response = await apiClient.get('/projects', {
      params: {
        type: params?.type,
        scope: params?.scope,
        viewScope: params?.viewScope,
        folderId: params?.folderId,
        contentType: params?.contentType,
        search: params?.search,
        // 后端使用 snake_case
        sortBy: params?.sortBy || 'updated_at',
        sortOrder: params?.sortOrder || 'desc',
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
      },
      ...(activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : {}),
    });
    // 转换 snake_case 为 camelCase
    return keysToCamelCase<ProjectListResponse>(response);
  },

  /**
   * 获取单个项目
   * GET /api/v1/projects/:id
   */
  async getById(id: string, request?: { activeOrgId?: string | null }): Promise<Project> {
    const activeOrgId = request?.activeOrgId;
    return apiClient.get(
      `/projects/${id}`,
      activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : undefined
    ) as Promise<Project>;
  },

  async getEntryContext(
    id: string,
    request?: { activeOrgId?: string | null }
  ): Promise<ProjectEntryContext> {
    const activeOrgId = request?.activeOrgId;
    const response = await apiClient.get(
      `/projects/${id}/entry-context`,
      activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<ProjectEntryContext>(response);
  },

  async createEmptyEpisode(
    parentProjectId: string,
    data?: {
      description?: string | null;
      memberSource?: CreateSubprojectMemberSource;
      subprojectBootstrap?: SubprojectBootstrapPayload | null;
      copiedFromName?: string | null;
    },
    request?: { activeOrgId?: string | null }
  ): Promise<Project> {
    const activeOrgId = request?.activeOrgId;
    return apiClient.post(
      `/projects/${parentProjectId}/subprojects`,
      data ?? {},
      activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : undefined
    ) as Promise<Project>;
  },

  async getParentDirectory(parentProjectId: string): Promise<ParentDirectoryResponse> {
    const response = await apiClient.get(`/projects/${parentProjectId}/directory`);
    return keysToCamelCase<ParentDirectoryResponse>(response);
  },

  async getDirectorySubprojects(
    parentProjectId: string,
    params?: {
      folderId?: string | null;
      page?: number;
      pageSize?: number;
      sort?: DirectorySubprojectSort;
      focusSubprojectId?: string | null;
    }
  ): Promise<DirectorySubprojectsResponse> {
    const response = await apiClient.get(`/projects/${parentProjectId}/directory/subprojects`, {
      params: {
        ...(params?.folderId ? { folderId: params.folderId } : {}),
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 100,
        sort: params?.sort ?? 'display_order_desc',
        ...(params?.focusSubprojectId ? { focusSubprojectId: params.focusSubprojectId } : {}),
      },
    });
    return keysToCamelCase<DirectorySubprojectsResponse>(response);
  },

  async moveSubprojectOrderAnchor(
    subprojectId: string,
    payload: { beforeSubprojectId?: string; afterSubprojectId?: string }
  ): Promise<{ id: string; displayOrder: number; folderId?: string | null }> {
    const response = await apiClient.post(`/projects/${subprojectId}/order-anchor`, payload);
    return keysToCamelCase(response);
  },

  async getSubprojects(
    parentProjectId: string,
    params?: {
      page?: number;
      pageSize?: number;
      includeArchived?: boolean;
    }
  ): Promise<ProjectListResponse> {
    return apiClient.get(`/projects/${parentProjectId}/subprojects`, {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 100,
        ...(params?.includeArchived === undefined
          ? {}
          : { includeArchived: params.includeArchived }),
      },
    }) as Promise<ProjectListResponse>;
  },

  /**
   * 创建项目
   * POST /api/v1/projects
   */
  async create(
    data: CreateProjectDto,
    request?: { activeOrgId?: string | null }
  ): Promise<Project> {
    const activeOrgId = request?.activeOrgId;
    return apiClient.post(
      '/projects',
      data,
      activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : undefined
    ) as Promise<Project>;
  },

  /**
   * 更新项目
   * PUT /api/v1/projects/:id
   */
  async update(id: string, data: UpdateProjectDto): Promise<Project> {
    return apiClient.put(`/projects/${id}`, data) as Promise<Project>;
  },

  async getOrgProducerSetup(id: string, activeOrgId: string): Promise<ProducerSetupResponse> {
    const response = await apiClient.get(`/projects/${id}/enterprise/producer-setup`, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    });
    return keysToCamelCase<ProducerSetupResponse>(response);
  },

  async submitOrgProducerSetup(
    id: string,
    data: ProducerSetupRequest,
    activeOrgId: string
  ): Promise<ProducerSetupResponse> {
    const response = await apiClient.put(`/projects/${id}/enterprise/producer-setup`, data, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    });
    return keysToCamelCase<ProducerSetupResponse>(response);
  },

  async getOrgDirectorSetup(id: string, activeOrgId: string): Promise<DirectorBaseSetupResponse> {
    const response = await apiClient.get(`/projects/${id}/enterprise/director-setup`, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    });
    return keysToCamelCase<DirectorBaseSetupResponse>(response);
  },

  async submitOrgDirectorSetup(
    id: string,
    data: DirectorBaseSetupRequest,
    activeOrgId: string
  ): Promise<DirectorBaseSetupResponse> {
    const response = await apiClient.put(`/projects/${id}/enterprise/director-setup`, data, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    });
    return keysToCamelCase<DirectorBaseSetupResponse>(response);
  },

  async completeOrgMemberSetup(
    id: string,
    data: MemberSetupCompleteRequest,
    activeOrgId: string
  ): Promise<MemberSetupCompleteResponse> {
    const response = await apiClient.put(`/projects/${id}/enterprise/member-setup/complete`, data, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    });
    return keysToCamelCase<MemberSetupCompleteResponse>(response);
  },

  async updateMemberConfigInheritance(
    parentProjectId: string,
    data: MemberConfigInheritanceRequest
  ): Promise<MemberConfigInheritanceSummary> {
    const response = await apiClient.patch(
      `/projects/${parentProjectId}/member-config-inheritance`,
      data
    );
    return keysToCamelCase<MemberConfigInheritanceSummary>(response);
  },

  async setSubprojectAsMemberConfigDefaultSource(
    projectId: string
  ): Promise<MemberConfigInheritanceSummary> {
    const response = await apiClient.put(
      `/projects/${projectId}/member-config-inheritance/default-source`,
      {}
    );
    return keysToCamelCase<MemberConfigInheritanceSummary>(response);
  },

  async getSubprojectMemberTargets(
    parentProjectId: string
  ): Promise<SubprojectMemberTargetListResponse> {
    const response = await apiClient.get(`/projects/${parentProjectId}/subprojects/member-targets`);
    return keysToCamelCase<SubprojectMemberTargetListResponse>(response);
  },

  async batchAddMembersToSubprojects(
    parentProjectId: string,
    data: BatchAddSubprojectMembersRequest,
    request?: { activeOrgId?: string | null }
  ): Promise<BatchAddSubprojectMembersResponse> {
    const activeOrgId = request?.activeOrgId;
    const response = await apiClient.post(
      `/projects/${parentProjectId}/subprojects/members/batch-add`,
      data,
      activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<BatchAddSubprojectMembersResponse>(response);
  },

  async getSubprojectMemberManagement(
    parentProjectId: string,
    activeOrgId: string
  ): Promise<ParentProjectMemberManagementContext> {
    const response = await apiClient.get(
      `/projects/${parentProjectId}/subprojects/member-management`,
      {
        headers: { 'X-Active-Org-Id': activeOrgId },
      }
    );
    return keysToCamelCase<ParentProjectMemberManagementContext>(response);
  },

  async batchRemoveMembersFromSubprojects(
    parentProjectId: string,
    data: BatchRemoveSubprojectMembersRequest,
    activeOrgId: string
  ): Promise<BatchRemoveSubprojectMembersResponse> {
    const response = await apiClient.post(
      `/projects/${parentProjectId}/subprojects/members/batch-remove`,
      data,
      {
        headers: { 'X-Active-Org-Id': activeOrgId },
      }
    );
    return keysToCamelCase<BatchRemoveSubprojectMembersResponse>(response);
  },

  async listScreenplayCreateGrants(parentProjectId: string): Promise<{
    hostMembers: Array<{ userId: string; canCreateScreenplays: boolean }>;
    channelOrgs: Array<{ orgId: string; pcoId: string; canCreateScreenplays: boolean }>;
  }> {
    const response = await apiClient.get(`/projects/${parentProjectId}/screenplay-create-grants`);
    return keysToCamelCase(response);
  },

  async upsertScreenplayCreateGrant(
    parentProjectId: string,
    data: { userId?: string; orgId?: string; joinEpisodeId?: string }
  ): Promise<{
    hostMembers: Array<{ userId: string; canCreateScreenplays: boolean }>;
    channelOrgs: Array<{ orgId: string; pcoId: string; canCreateScreenplays: boolean }>;
  }> {
    const response = await apiClient.put(
      `/projects/${parentProjectId}/screenplay-create-grants`,
      data
    );
    return keysToCamelCase(response);
  },

  async revokeScreenplayCreateGrant(
    parentProjectId: string,
    data: { userId?: string; orgId?: string }
  ): Promise<void> {
    await apiClient.delete(`/projects/${parentProjectId}/screenplay-create-grants`, { data });
  },

  async batchUpdateSubprojectMemberRoles(
    parentProjectId: string,
    data: BatchUpdateSubprojectMemberRolesRequest,
    activeOrgId: string
  ): Promise<BatchUpdateSubprojectMemberRolesResponse> {
    const response = await apiClient.patch(
      `/projects/${parentProjectId}/subprojects/members/batch-update-roles`,
      data,
      {
        headers: { 'X-Active-Org-Id': activeOrgId },
      }
    );
    return keysToCamelCase<BatchUpdateSubprojectMemberRolesResponse>(response);
  },

  async updatePcoSubprojectMemberRoles(
    parentProjectId: string,
    projectId: string,
    userId: string,
    data: {
      functionalRoles: string[];
      canManageMembers?: boolean | null;
      canCreateScreenplays?: boolean | null;
    },
    activeOrgId: string
  ): Promise<ProjectMember> {
    const response = await apiClient.patch(
      `/projects/${parentProjectId}/subprojects/${projectId}/members/${userId}/roles`,
      data,
      {
        headers: { 'X-Active-Org-Id': activeOrgId },
      }
    );
    return keysToCamelCase<ProjectMember>(response);
  },

  /**
   * 子项目消耗上限（父持总池；RFC-003）
   * PATCH /api/v1/projects/:id/credits-budget
   */
  async patchSubprojectCreditsBudget(
    id: string,
    body: { aiCreditsBudget?: number | null },
    activeOrgId: string
  ): Promise<{
    projectId: string;
    aiCreditsBudget: number;
    budgetConsumed: number;
  }> {
    return apiClient.patch(`/projects/${id}/credits-budget`, body, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    }) as Promise<{
      projectId: string;
      aiCreditsBudget: number;
      budgetConsumed: number;
    }>;
  },

  /**
   * 设置项目封面
   * PUT /api/v1/projects/:id/cover-image
   */
  async setCoverImage(id: string, data: SetProjectCoverImageDto): Promise<Project> {
    return apiClient.put(`/projects/${id}/cover-image`, data) as Promise<Project>;
  },

  /**
   * 删除项目（移至回收站）
   * DELETE /api/v1/projects/:id
   */
  async delete(id: string): Promise<{ message: string }> {
    return apiClient.delete(`/projects/${id}`) as Promise<{ message: string }>;
  },

  /**
   * 删除项目下的单集（不进入回收站）
   * DELETE /api/v1/projects/:id/episode
   */
  async getEpisodeDeletePreview(id: string): Promise<EpisodeDeletePreview> {
    return apiClient.get(`/projects/${id}/episode/delete-preview`) as Promise<EpisodeDeletePreview>;
  },

  async deleteEpisode(id: string, options?: EpisodeDeleteOptions): Promise<{ message: string }> {
    return apiClient.delete(`/projects/${id}/episode`, {
      data: {
        deleteCreatedAssets: Boolean(options?.deleteCreatedAssets),
        deleteCreatedMaterials: Boolean(options?.deleteCreatedMaterials),
      },
    }) as Promise<{ message: string }>;
  },

  /**
   * 归档项目
   * POST /api/v1/projects/:id/archive
   */
  async archiveProject(
    id: string,
    data?: ArchiveProjectDto,
    request?: { activeOrgId?: string | null }
  ): Promise<ArchiveProjectResponse> {
    const response = await apiClient.post(
      `/projects/${id}/archive`,
      data ?? {},
      request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId')
        ? { headers: { 'X-Active-Org-Id': request.activeOrgId ?? '' } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<ArchiveProjectResponse>(response);
  },

  async pauseProject(
    id: string,
    data?: PauseProjectDto,
    request?: { activeOrgId?: string | null }
  ): Promise<PauseProjectResponse> {
    const response = await apiClient.post(
      `/projects/${id}/pause`,
      data ?? {},
      request?.activeOrgId
        ? { headers: { 'X-Active-Org-Id': request.activeOrgId } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<PauseProjectResponse>(response);
  },

  async resumeProject(
    id: string,
    request?: { activeOrgId?: string | null }
  ): Promise<PauseProjectResponse> {
    const response = await apiClient.post(
      `/projects/${id}/resume`,
      {},
      request?.activeOrgId
        ? { headers: { 'X-Active-Org-Id': request.activeOrgId } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<PauseProjectResponse>(response);
  },

  /**
   * 解档项目
   * POST /api/v1/projects/:id/unarchive
   */
  async unarchiveProject(
    id: string,
    request?: { activeOrgId?: string | null }
  ): Promise<ArchiveProjectResponse> {
    const response = await apiClient.post(
      `/projects/${id}/unarchive`,
      {},
      request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId')
        ? { headers: { 'X-Active-Org-Id': request.activeOrgId ?? '' } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<ArchiveProjectResponse>(response);
  },

  /**
   * 移动项目到文件夹
   * POST /api/v1/projects/:id/move
   */
  async move(id: string, data: MoveProjectDto): Promise<Project> {
    return apiClient.post(`/projects/${id}/move`, data) as Promise<Project>;
  },

  /**
   * 克隆项目
   * POST /api/v1/projects/:id/clone
   */
  async clone(
    id: string,
    data?: { targetName?: string; targetFolderId?: string }
  ): Promise<{
    id: string;
    name: string;
    clonedFrom: string;
    createdAt: string;
  }> {
    return apiClient.post(`/projects/${id}/clone`, data) as Promise<{
      id: string;
      name: string;
      clonedFrom: string;
      createdAt: string;
    }>;
  },

  /**
   * 创建项目导出任务
   * POST /api/v1/projects/:id/export
   */
  async createExportJob(
    id: string,
    payload: ProjectExportRequest
  ): Promise<CreateProjectExportResponse> {
    const response = await apiClient.post(`/projects/${id}/export`, payload);
    return keysToCamelCase<CreateProjectExportResponse>(response);
  },

  async getExportCapabilities(id: string): Promise<ProjectExportCapabilitiesResponse> {
    const response = await apiClient.get(`/projects/${id}/export/capabilities`);
    return keysToCamelCase<ProjectExportCapabilitiesResponse>(response);
  },

  /**
   * 获取项目导出任务状态
   * GET /api/v1/projects/export/:exportJobId
   */
  async getExportJobStatus(exportJobId: string): Promise<ProjectExportStatusResponse> {
    const response = await apiClient.get(`/projects/export/${exportJobId}`);
    return keysToCamelCase<ProjectExportStatusResponse>(response);
  },

  /**
   * 获取项目导出 zip 的公开域下载 URL
   * GET /api/v1/projects/export/:exportJobId/download-url
   */
  async getExportJobDownloadUrl(exportJobId: string): Promise<ProjectExportDownloadUrlResponse> {
    const response = await apiClient.get(`/projects/export/${exportJobId}/download-url`);
    return keysToCamelCase<ProjectExportDownloadUrlResponse>(response);
  },

  /**
   * 更新项目顺序
   * PUT /api/v1/projects/:id/order
   */
  async updateOrder(id: string, displayOrder: number): Promise<Project> {
    return apiClient.put(`/projects/${id}/order`, {
      displayOrder,
    }) as Promise<Project>;
  },

  // ========================================================================
  // 回收站相关
  // ========================================================================

  /**
   * 获取回收站列表
   * GET /api/v1/projects/trash
   */
  async getTrash(params?: { page?: number; pageSize?: number }): Promise<TrashListResponse> {
    return apiClient.get('/projects/trash', {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
      },
    }) as Promise<TrashListResponse>;
  },

  async getFunctionalRoles(): Promise<FunctionalRoleDefinition[]> {
    const response = await apiClient.get('/projects/functional-roles');
    return keysToCamelCase<FunctionalRoleDefinition[]>(response);
  },

  /**
   * 从回收站恢复项目
   * POST /api/v1/projects/:id/restore
   */
  async restore(id: string): Promise<RestoreProjectResponse> {
    return apiClient.post(`/projects/${id}/restore`) as Promise<RestoreProjectResponse>;
  },

  /**
   * 永久删除项目（从回收站）
   * DELETE /api/v1/projects/:id/permanent
   */
  async permanentDelete(id: string): Promise<{ message: string }> {
    return apiClient.delete(`/projects/${id}/permanent`) as Promise<{ message: string }>;
  },

  // ========================================================================
  // 分享相关
  // ========================================================================

  /**
   * 创建项目分享
   * POST /api/v1/projects/:id/shares
   */
  async createShare(
    id: string,
    data: {
      password?: string;
      permission?: SharePermission;
      expiresAt?: string;
      maxAccessCount?: number;
      contentScope?: string;
    }
  ): Promise<ProjectShare> {
    const response = await apiClient.post(`/projects/${id}/shares`, data);
    return keysToCamelCase<ProjectShare>(response);
  },

  /**
   * 获取项目分享列表
   * GET /api/v1/projects/:id/shares
   */
  async getShares(id: string): Promise<ProjectShareListResponse> {
    const response = await apiClient.get(`/projects/${id}/shares`);
    return keysToCamelCase<ProjectShareListResponse>(response);
  },

  /**
   * 撤销项目分享
   * DELETE /api/v1/projects/:id/shares/:shareId
   */
  async revokeShare(id: string, shareId: string): Promise<{ message: string }> {
    return apiClient.delete(`/projects/${id}/shares/${shareId}`) as Promise<{ message: string }>;
  },

  async updateShareSettings(
    id: string,
    shareId: string,
    data: {
      password?: string;
    }
  ): Promise<ProjectShare> {
    const response = await apiClient.patch(`/projects/${id}/shares/${shareId}/settings`, data);
    return keysToCamelCase<ProjectShare>(response);
  },

  async regenerateShareLink(id: string, shareId: string): Promise<ProjectShare> {
    const response = await apiClient.patch(`/projects/${id}/shares/${shareId}/regenerate`);
    return keysToCamelCase<ProjectShare>(response);
  },

  // ========================================================================
  // 成员管理
  // ========================================================================

  /**
   * 获取项目成员列表
   * GET /api/v1/projects/:id/members
   */
  async getMembers(
    id: string,
    params?: { page?: number; pageSize?: number; includeAllParticipants?: boolean }
  ): Promise<ProjectMemberListResponse> {
    const response = await apiClient.get(`/projects/${id}/members`, {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        ...(params?.includeAllParticipants ? { includeAllParticipants: true } : {}),
      },
    });
    return keysToCamelCase<ProjectMemberListResponse>(response);
  },

  /**
   * 添加项目成员（RFC-003 §6.1）
   * POST /api/v1/projects/:id/members
   *
   * - 默认项目成员管理流程只提交项目职能；
   * - `groupIds` 仅保留给高级项目授权 / 兼容入口，一次完成加人 + 挂载权限组；
   * - 聚合节点调用将返回 409 `PROJECT_ADD_MEMBER_TO_AGGREGATE_NODE`。
   */
  async addMember(
    id: string,
    data: {
      userId: string;
      role: 'owner' | 'editor' | 'viewer';
      groupIds?: string[] | null;
      functionalRoles?: string[] | null;
      canManageMembers?: boolean;
      canCreateScreenplays?: boolean;
      canDownload?: boolean;
    }
  ): Promise<ProjectMember> {
    const response = await apiClient.post(`/projects/${id}/members`, data);
    return keysToCamelCase<ProjectMember>(response);
  },

  /**
   * 更新项目成员（RFC-003 §6.1）
   * PATCH /api/v1/projects/:id/members/:userId
   *
   * - 传 `role` 更新角色；
   * - 默认项目成员管理流程只更新项目职能和导演附加管理开关；
   * - 传 `groupIds` 全量覆盖高级授权挂载（空数组=清空；`undefined`/`null` 不改）。
   */
  async updateMember(
    id: string,
    userId: string,
    data: {
      role?: 'owner' | 'editor' | 'viewer';
      groupIds?: string[] | null;
      functionalRoles?: string[] | null;
      canManageMembers?: boolean | null;
      canCreateScreenplays?: boolean | null;
      canDownload?: boolean | null;
    }
  ): Promise<ProjectMember> {
    const response = await apiClient.patch(`/projects/${id}/members/${userId}`, data);
    return keysToCamelCase<ProjectMember>(response);
  },

  /**
   * 移除项目成员
   * DELETE /api/v1/projects/:id/members/:userId
   *
   * 同事务级联清理该成员的 `project_member_permission_groups`（RFC-003 §10.11）。
   */
  async removeMember(id: string, userId: string): Promise<{ message: string }> {
    return apiClient.delete(`/projects/${id}/members/${userId}`) as Promise<{ message: string }>;
  },

  async removeAdminParticipation(id: string, userId: string): Promise<{ message: string }> {
    return apiClient.delete(`/projects/${id}/enterprise/admin-participation/${userId}`) as Promise<{
      message: string;
    }>;
  },

  async replaceEnterpriseProjectProducer(
    id: string,
    data: ReplaceProjectProducerDto,
    activeOrgId?: string | null
  ): Promise<ProjectProducerReplaceResponse> {
    const response = await apiClient.patch(
      `/projects/${id}/enterprise/producer`,
      data,
      activeOrgId
        ? { headers: { 'X-Active-Org-Id': activeOrgId } as Record<string, string> }
        : undefined
    );
    return keysToCamelCase<ProjectProducerReplaceResponse>(response);
  },

  /**
   * 获取项目可用的业务权限组（RFC-003 §6.2）
   * GET /api/v1/projects/:id/permission-groups
   */
  async getPermissionGroups(id: string): Promise<ProjectPermissionGroupOption[]> {
    const response = await apiClient.get(`/projects/${id}/permission-groups`);
    return keysToCamelCase<ProjectPermissionGroupOption[]>(response);
  },

  /**
   * 为成员挂载单个权限组
   * POST /api/v1/projects/:id/members/:userId/permission-groups
   */
  async mountMemberPermissionGroup(
    id: string,
    userId: string,
    groupId: string
  ): Promise<{ message: string }> {
    return apiClient.post(`/projects/${id}/members/${userId}/permission-groups`, {
      groupId,
    }) as Promise<{ message: string }>;
  },

  /**
   * 为成员解绑单个权限组
   * DELETE /api/v1/projects/:id/members/:userId/permission-groups/:groupId
   */
  async unmountMemberPermissionGroup(
    id: string,
    userId: string,
    groupId: string
  ): Promise<{ message: string }> {
    return apiClient.delete(
      `/projects/${id}/members/${userId}/permission-groups/${groupId}`
    ) as Promise<{ message: string }>;
  },

  /**
   * 将私人项目移动至团队/企业
   * POST /api/v1/projects/:id/transfer-to-org
   */
  async transferToOrg(
    id: string,
    data: TransferProjectToOrgDto
  ): Promise<TransferProjectToOrgResponse> {
    const response = await apiClient.post(`/projects/${id}/transfer-to-org`, data);
    return keysToCamelCase<TransferProjectToOrgResponse>(response);
  },

  async getPublicShareInfo(token: string): Promise<PublicProjectShareInfo> {
    const response = await apiClient.get(`/public/shared/p/${token}/info`);
    return keysToCamelCase<PublicProjectShareInfo>(response);
  },

  async verifyPublicSharePassword(
    token: string,
    password: string
  ): Promise<{ valid: boolean; message: string }> {
    const response = await apiClient.post(`/public/shared/p/${token}/verify-password`, undefined, {
      params: { password },
    });
    return keysToCamelCase<{ valid: boolean; message: string }>(response);
  },

  async accessPublicShare(token: string, password?: string): Promise<PublicProjectShareAccess> {
    const response = await apiClient.get(`/public/shared/p/${token}`, {
      params: password ? { password } : undefined,
    });
    return keysToCamelCase<PublicProjectShareAccess>(response);
  },

  // ========================================================================
  // 跨组织协作（PCO，RFC-004）
  // ========================================================================

  /** 查看项目的协作组织列表 */
  async listCollaboratorOrgs(projectId: string): Promise<CollaboratorOrgListResponse> {
    const response = await apiClient.get(`/projects/${projectId}/collaborators/orgs`);
    return keysToCamelCase<CollaboratorOrgListResponse>(response);
  },

  /** 邀请一个组织协作 */
  async inviteCollaboratorOrg(
    projectId: string,
    data: InviteCollaboratorOrgDto
  ): Promise<CollaboratorOrg> {
    const response = await apiClient.post(`/projects/${projectId}/collaborators/orgs`, data);
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  async batchInviteCollaboratorOrg(
    parentProjectId: string,
    data: BatchInviteCollaboratorOrgDto
  ): Promise<BatchInviteCollaboratorOrgResult> {
    const response = await apiClient.post(
      `/projects/${parentProjectId}/collaborators/orgs/batch-invite`,
      data
    );
    return keysToCamelCase<BatchInviteCollaboratorOrgResult>(response);
  },

  /** 接受协作邀请 */
  async acceptCollaboratorOrg(pcoId: string): Promise<CollaboratorOrg> {
    const response = await apiClient.post(`/projects/collaborators/orgs/${pcoId}/accept`);
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  /** 拒绝协作邀请 */
  async rejectCollaboratorOrg(pcoId: string): Promise<CollaboratorOrg> {
    const response = await apiClient.post(`/projects/collaborators/orgs/${pcoId}/reject`);
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  /** 撤回尚未接受的承制邀请，不生成承制报告 */
  async withdrawCollaboratorOrg(projectId: string, pcoId: string): Promise<CollaboratorOrg> {
    const response = await apiClient.post(`/projects/${projectId}/collaborators/${pcoId}/withdraw`);
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  /** 解除承制关系并生成报告 */
  async terminateCollaboratorOrg(
    projectId: string,
    pcoId: string,
    data: ContractorSettlementReportTerminateDto
  ): Promise<ContractorSettlementReportTerminateResponse> {
    const response = await apiClient.post(
      `/projects/${projectId}/collaborators/${pcoId}/terminate`,
      data
    );
    return keysToCamelCase<ContractorSettlementReportTerminateResponse>(response);
  },

  /** 旧撤销入口兼容：必须传解除原因 */
  async revokeCollaboratorOrg(
    pcoId: string,
    data: ContractorSettlementReportTerminateDto
  ): Promise<ContractorSettlementReportTerminateResponse> {
    const response = await apiClient.delete(`/projects/collaborators/orgs/${pcoId}`, { data });
    return keysToCamelCase<ContractorSettlementReportTerminateResponse>(response);
  },

  async listContractorSettlementReports(
    projectId: string,
    activeOrgId: string
  ): Promise<ContractorSettlementReportListResponse> {
    const response = await apiClient.get(`/projects/${projectId}/contractor-settlement-reports`, {
      headers: { 'X-Active-Org-Id': activeOrgId },
    });
    return keysToCamelCase<ContractorSettlementReportListResponse>(response);
  },

  async getContractorSettlementReport(
    projectId: string,
    reportId: string,
    activeOrgId: string
  ): Promise<ContractorSettlementReportDetail> {
    const response = await apiClient.get(
      `/projects/${projectId}/contractor-settlement-reports/${reportId}`,
      {
        headers: { 'X-Active-Org-Id': activeOrgId },
      }
    );
    return keysToCamelCase<ContractorSettlementReportDetail>(response);
  },

  async updateCollaboratorOrgAllowedFunctionalRoles(
    projectId: string,
    pcoId: string,
    data: UpdateCollaboratorOrgAllowedFunctionalRolesDto
  ): Promise<CollaboratorOrg> {
    const response = await apiClient.patch(
      `/projects/${projectId}/collaborators/${pcoId}/allowed-functional-roles`,
      data
    );
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  /** 承制方侧添加本方成员到 PCO 项目 */
  async addCollaboratorOrgMember(
    projectId: string,
    pcoId: string,
    data: AddCollaboratorOrgMemberDto
  ): Promise<unknown> {
    const response = await apiClient.post(
      `/projects/${projectId}/collaborators/${pcoId}/members`,
      data
    );
    return keysToCamelCase(response);
  },

  async createDownloadIntent(
    projectId: string,
    data: {
      resourceType:
        | 'storyboard_image'
        | 'storyboard_video'
        | 'voiceover'
        | 'sound_effect'
        | 'character'
        | 'location'
        | 'prop';
      resourceId: string;
      objectKey?: string;
      fileName?: string;
    }
  ): Promise<{ url: string; fileName: string }> {
    const response = await apiClient.post(`/projects/${projectId}/downloads`, data);
    return keysToCamelCase<{ url: string; fileName: string }>(response);
  },

  async updateCollaboratorOrgManageAllAssets(
    projectId: string,
    pcoId: string,
    data: { canManageAllAssets: boolean }
  ): Promise<CollaboratorOrg> {
    const response = await apiClient.patch(
      `/projects/${projectId}/collaborators/${pcoId}/manage-all-assets`,
      data
    );
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  async updateCollaboratorOrgDownload(
    projectId: string,
    pcoId: string,
    data: { canDownload: boolean }
  ): Promise<CollaboratorOrg> {
    const response = await apiClient.patch(
      `/projects/${projectId}/collaborators/${pcoId}/download`,
      data
    );
    return keysToCamelCase<CollaboratorOrg>(response);
  },

  async listCollaboratorOrgMemberCandidates(
    projectId: string,
    pcoId: string
  ): Promise<CollaboratorOrgMemberCandidateListResponse> {
    const response = await apiClient.get(
      `/projects/${projectId}/collaborators/${pcoId}/members/candidates`
    );
    return keysToCamelCase<CollaboratorOrgMemberCandidateListResponse>(response);
  },

  async updateCollaboratorOrgMemberRoles(
    projectId: string,
    pcoId: string,
    userId: string,
    data: UpdateCollaboratorOrgMemberRolesDto
  ): Promise<ProjectMember> {
    const response = await apiClient.patch(
      `/projects/${projectId}/collaborators/${pcoId}/members/${userId}/roles`,
      data
    );
    return keysToCamelCase<ProjectMember>(response);
  },

  /** 当前活跃组织的待审批协作邀请 */
  async listPendingCollaboratorOrgs(): Promise<PendingCollaborationListResponse> {
    const response = await apiClient.get(`/projects/collaborators/orgs/pending`);
    return keysToCamelCase<PendingCollaborationListResponse>(response);
  },
};
